import { useEffect, useRef, useState } from 'react'
import {
  ContentDescriptionBoxGroupHome,
  ContentDescriptionBoxHome,
  ContentDescriptionBoxSecurityHome,
  ContentDescriptionBoxSecurityTextHome,
  ContentDescriptionBoxSecurityTitleHome,
  ContentDescriptionBoxTextHome,
  ContentDescriptionBoxTitleHome,
  ContentDescriptionHome,
  ContentDescriptionTextHome,
  ContentDescriptionTitleHome,
  ContentHome,
  ContentTitleHome
} from '../../assets/styles/styles'
import stores from '../../stores/store'
import { BusinessRounded, GppGood, PeopleAlt, Close } from '@mui/icons-material'
import { Drawer, IconButton } from '@mui/material'
import styled from 'styled-components'
import useMediaResponsive from '../../assets/styles/mediaQuery'

const StyledButtonsContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 8px;
  justify-content: center;
  background: ${(props) => props.theme.colors.background || 'transparent'};
  z-index: 900;
`

const StyledVerticalButton = styled(IconButton)`
  && {
    background: ${(props) => props.theme.colors.lightRed};
    color: ${(props) => props.theme.colors.white};
    border-radius: 8px 8px 0 0;
    padding: 12px 20px;
    font-weight: 600;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    &:hover {
      background: ${(props) => props.theme.colors.red};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

const StyledInfoDrawer = styled(Drawer)`
  && {
    .MuiPaper-root {
      background: ${(props) => props.theme.colors.graySemiDark};
      width: 450px;
      max-width: 90vw;
      padding: 24px;
    }
  }
`

const StyledDrawerHeader = styled.div`
  && {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid ${(props) => props.theme.colors.grayBorder};
  }
`

const StyledDrawerTitle = styled.div`
  && {
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${(props) => props.theme.colors.white};
    font-size: 24px;
    font-weight: 700;

    svg {
      width: 32px;
      height: 32px;
    }
  }
`

const StyledDrawerContent = styled.div`
  && {
    color: ${(props) => props.theme.colors.white};
    font-size: 16px;
    line-height: 1.6;
  }
`

const StyledCloseButton = styled(IconButton)`
  && {
    color: ${(props) => props.theme.colors.white};

    &:hover {
      color: ${(props) => props.theme.colors.red};
    }
  }
`

export default function Home() {
  const { jsonSelected: lang } = stores.DataLocaleStore()
  const { hideHeader, setHideHeader } = stores.DataHideHeaderStore()
  const listRef = useRef<HTMLDivElement>(null)

  const [openPeopleDrawer, setOpenPeopleDrawer] = useState(false)
  const [openCompanyDrawer, setOpenCompanyDrawer] = useState(false)

  const { maxMedia1250 } = useMediaResponsive()

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const element = listRef.current
      if (!element) return

      const isAtTop = element.scrollTop === 0
      const isAtBottom =
        element.scrollHeight - element.scrollTop === element.clientHeight

      if ((event.deltaY > 0 && isAtBottom) || (event.deltaY < 0 && isAtTop)) {
        setHideHeader(event.deltaY > 0)
      }
    }

    const element = listRef.current
    element?.addEventListener('wheel', handleWheel)

    return () => {
      element?.removeEventListener('wheel', handleWheel)
    }
  }, [])

  return (
    <>
      <ContentHome ref={listRef} $hideHeader={hideHeader}>
        <ContentTitleHome>{lang.home.welcome}</ContentTitleHome>

        <ContentDescriptionHome>
          <ContentDescriptionTitleHome>
            {lang.home.title}
          </ContentDescriptionTitleHome>

          <ContentDescriptionTextHome
            dangerouslySetInnerHTML={{ __html: lang.home.description1 }}
          />
          <ContentDescriptionTextHome
            dangerouslySetInnerHTML={{ __html: lang.home.description2 }}
          />
        </ContentDescriptionHome>

        {!maxMedia1250 && (
          <ContentDescriptionBoxGroupHome>
            <ContentDescriptionBoxHome>
              <ContentDescriptionBoxTitleHome>
                <PeopleAlt />
                {lang.home.titleBox1}
              </ContentDescriptionBoxTitleHome>
              <ContentDescriptionBoxTextHome>
                {lang.home.descriptionBox1}
              </ContentDescriptionBoxTextHome>
            </ContentDescriptionBoxHome>

            <ContentDescriptionBoxHome>
              <ContentDescriptionBoxTitleHome>
                <BusinessRounded />
                {lang.home.titleBox2}
              </ContentDescriptionBoxTitleHome>
              <ContentDescriptionBoxTextHome>
                {lang.home.descriptionBox2}
              </ContentDescriptionBoxTextHome>
            </ContentDescriptionBoxHome>
          </ContentDescriptionBoxGroupHome>
        )}

        <ContentDescriptionBoxSecurityHome>
          <ContentDescriptionBoxSecurityTitleHome>
            <GppGood />
            {lang.home.titleSecurity}
          </ContentDescriptionBoxSecurityTitleHome>
          <ContentDescriptionBoxSecurityTextHome>
            {lang.home.descriptionSecurity}
          </ContentDescriptionBoxSecurityTextHome>
        </ContentDescriptionBoxSecurityHome>
      </ContentHome>

      {maxMedia1250 && (
        <StyledButtonsContainer>
          <StyledVerticalButton onClick={() => setOpenPeopleDrawer(true)}>
            <PeopleAlt />
            {lang.home.titleBox1.replace(':', '')}
          </StyledVerticalButton>

          <StyledVerticalButton onClick={() => setOpenCompanyDrawer(true)}>
            <BusinessRounded />
            {lang.home.titleBox2.replace(':', '')}
          </StyledVerticalButton>
        </StyledButtonsContainer>
      )}

      <StyledInfoDrawer
        anchor="right"
        open={openPeopleDrawer}
        onClose={() => setOpenPeopleDrawer(false)}
      >
        <StyledDrawerHeader>
          <StyledDrawerTitle>
            <PeopleAlt />
            {lang.home.titleBox1}
          </StyledDrawerTitle>
          <StyledCloseButton onClick={() => setOpenPeopleDrawer(false)}>
            <Close />
          </StyledCloseButton>
        </StyledDrawerHeader>
        <StyledDrawerContent>{lang.home.descriptionBox1}</StyledDrawerContent>
      </StyledInfoDrawer>

      <StyledInfoDrawer
        anchor="right"
        open={openCompanyDrawer}
        onClose={() => setOpenCompanyDrawer(false)}
      >
        <StyledDrawerHeader>
          <StyledDrawerTitle>
            <BusinessRounded />
            {lang.home.titleBox2}
          </StyledDrawerTitle>
          <StyledCloseButton onClick={() => setOpenCompanyDrawer(false)}>
            <Close />
          </StyledCloseButton>
        </StyledDrawerHeader>
        <StyledDrawerContent>{lang.home.descriptionBox2}</StyledDrawerContent>
      </StyledInfoDrawer>
    </>
  )
}
